import axios from 'axios'
import { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation, TFunction } from 'react-i18next'
import useLoadingStore from '../../stores/useLoadingStore'
import useRelogin from '../useRelogin'
import ConfigAPI from '../../constant/ConfigAPI'
import { useSelector } from 'react-redux'
import { RootStateType } from '../../redux/reducer/rootReducer'

type ParamServerSideType = {
    pageNumber: number
    rowPerPage: number
    sortField: string
    sortType: string
    keyword: string
    groupId: string
}

type ChannelType = {
    url: string
    no: number
    name: string
}

export interface Form {
    owner?: string
    channels?: ChannelType[]
}

export const validate = async (values: Form, t: TFunction<'translation'>) => {
    const errors: Partial<Form> = {}
    if (!values.owner) {
        errors.owner = `${t('LABEL.TEXT_REQUIRED')}`
    }
    return errors
}

const initial = {
    remove: undefined,
    index: 0,
    value: '0',
}

const useContent = () => {
    const itemData = useRef<{ remove?: (index: number) => void; index: number; value: string }>(initial)
    const valueSubmitted = useRef<Form>()
    const [initialData, setInitialData] = useState<CMSContentFormType>({})
    const [dataList, setDataList] = useState<MuxListType[]>([])
    const [isDuplicate, setIsDuplicate] = useState<boolean>(false)
    const [openDialogBack, setOpenDialogBack] = useState<boolean>(false)
    const [openDialogSave, setOpenDialogSave] = useState<boolean>(false)
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [alertMessageError, setAlertMessageError] = useState<string>('')
    const [statusCode, setStatusCode] = useState<number>()
    const [actionType, setActionType] = useState<string>('')
    const [data, setData] = useState<any[]>([])
    const [totalItem, setTotalItem] = useState<number>(0)
    const [paramServerSide, setParamServerSide] = useState<ParamServerSideType>({
        pageNumber: 1,
        rowPerPage: 10,
        sortField: '',
        sortType: '',
        keyword: '',
        groupId: '',
    })
    const { reLoginForTokenExpired, callServiceReLogin } = useRelogin()
    const { t } = useTranslation()
    const history = useHistory()
    const loadingStore = useLoadingStore()
    const { isLoggedIn } = useSelector(({ profile }: RootStateType) => profile)

    useEffect(() => {
        if (isLoggedIn) {
            callServiceList()
        }
    }, [isLoggedIn])

    const callServiceList = async () => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/content/manage/mux/list`
            const result = await axios.get(url, ConfigAPI)
            if (result) {
                setTotalItem(result.data.length)
                setDataList(result.data)
                setData(
                    result.data.map((item: MuxListType) => ({
                        muxId: item.muxId,
                        muxName: item.muxName,
                        provider: item.provider,
                    })),
                )
            }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired('/backoffice/mux-management')
                    //callServiceReLogin('/backoffice/mux-management')
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceGetById = async (id: number) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/content/manage/mux/${id}`
            const result = await axios.get(url, ConfigAPI)
            if (result.data) {
                setStatusCode(200)
                setInitialData(result.data)
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.NOT_RETRIEVE_DATA'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceEdit = async (id: number, data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/content/manage/mux/${id}`
            const chList = data.channels?.filter((item: ChannelType) => item.no && item.name)
            const reqParams = {
                provider: data.owner,
                channels:
                    chList && chList?.length > 0
                        ? chList?.map((item: ChannelType) => ({
                              urlLogo: item.url,
                              no: item.no,
                              name: item.name,
                          }))
                        : [],
            }
            const result = await axios.put(url, reqParams, ConfigAPI)
            if (result.data.message === 'success') {
                setStatusCode(200)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_SUCCESS'))
            } else if (result.data.message === 'duplicated') {
                setStatusCode(200)
                setIsDuplicate(true)
                setAlertMessageOk(t('ALERT.CONTENT.SAVE_DUPLICATED'))
            } else {
                setStatusCode(200)
                setAlertMessageOk(result.data.message)
            }
            setOpenDialogOk(true)

            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setAlertMessageError(t('ALERT.CONTENT.SAVE_NOT_SUCCESS'))
                    setOpenDialogError(true)
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const handleSubmitForm = (values: any) => {
        valueSubmitted.current = values
        setActionType('save')
        setOpenDialogSave(true)
    }

    const handleClickEdit = async (data: any) => {
        history.push(`/backoffice/mux-management/${data?.muxId}/edit`)
    }

    const handleClickForceBack = () => {
        history.push('/backoffice/mux-management')
    }

    const handleClickBack = (dirty: boolean) => {
        // ตอนกดปุ่มยกเลิกที่ form
        if (dirty) {
            setOpenDialogBack(true)
        } else {
            setOpenDialogBack(false)
            history.push('/backoffice/mux-management')
        }
        setActionType('back')
    }

    const handleClickCancelBack = () => {
        setOpenDialogBack(false)
    }

    const handleClickConfirmBack = () => {
        setOpenDialogBack(false)
        history.push('/backoffice/mux-management')
    }

    const handleClickCancelSave = () => {
        setOpenDialogSave(false)
    }

    const handleClickConfirmSave = (id: number) => {
        callServiceEdit(id, valueSubmitted.current as Form)
        setOpenDialogSave(false)
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
        setOpenDialogError(false)
        if (statusCode === 401) {
            history.push('/login')
        } else if (!isDuplicate && statusCode !== 500) {
            history.push('/backoffice/mux-management')
        }
        setIsDuplicate(false)
    }

    const handleClickDeleteFieldArrayItem = (remove: (index: number) => void, index: number, value: string) => {
        itemData.current = { remove, index, value }
        remove(index)
    }

    return {
        onSubmitForm: handleSubmitForm,
        onClickDeleteFieldArrayItem: handleClickDeleteFieldArrayItem,
        onClickEdit: handleClickEdit,
        onClickCancelBack: handleClickCancelBack,
        onClickCancelSave: handleClickCancelSave,
        onClickConfirmBack: handleClickConfirmBack,
        onClickConfirmSave: handleClickConfirmSave,
        onClickForceBack: handleClickForceBack,
        onClickBack: handleClickBack,
        onClickOk: handleClickOk,
        callServiceGetById,
        dataList,
        data,
        totalItem,
        paramServerSide,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
        actionType,
    }
}

export default useContent
